import { useEffect, useState } from 'react';
import WeeklyHours from './WeeklyHours.jsx';
import useBusinessPostApi from '../../lib/useBusinessPostApi.js';
import * as Toast from '../../lib/Toast.js';
import SpecificHours from './SpecificHours.jsx';
import AvailabilityGroups from './AvailabilityGroups.jsx';
import {
    setAvailabilityGroupsQuery,
    setBookableResourcesQuery,
} from '../../lib/Queries';
import { useCompleteStep } from '../../lib/Onboarding.js';
import AvailabilitySettings from '~/components/scheduling/AvailabilitySettings';

const ResourceAvailability = ({ resource }) => {
    const [defaultAvailability, setDefaultAvailability] = useState(
        resource.default_availability,
    );
    const [settings, setSettings] = useState(
        resource.settings?.availability_settings,
    );
    const [availabilityGroup, setAvailabilityGroup] = useState(null);
    const [isAvailabilityDirty, setIsAvailabilityDirty] = useState(false);
    const { isSaving, save } = useBusinessPostApi(
        `/scheduling/availability/${resource.id}`,
    );
    const [isEditing, setIsEditing] = useState(false);
    const { complete } = useCompleteStep();

    useEffect(() => {
        setDefaultAvailability(resource.default_availability);
        setSettings(resource.settings?.availability_settings);
        setAvailabilityGroup(null);
        setIsAvailabilityDirty(false);
    }, [resource.id]);

    function submit(ev) {
        ev?.preventDefault();
        save({
            default_availability: defaultAvailability,
            availabilityGroup: availabilityGroup,
            settings: settings,
        }).then(result => {
            setIsEditing(false);
            setIsAvailabilityDirty(false);
            Toast.success('Successfully saved availability');
            setBookableResourcesQuery(result.bookableResources, true);
            setAvailabilityGroupsQuery(resource.id, result.availabilityGroups);
            complete('scheduling-availability');
        });
    }

    return (
        <div>
            <AvailabilityGroups
                resource={resource}
                availabilityGroupId={availabilityGroup}
                setAvailability={({
                    availabilityGroupId,
                    defaultAvailability,
                    settings: groupSettings,
                }) => {
                    if (
                        !isAvailabilityDirty ||
                        confirm(
                            'You have unsaved hours.\n\nAre you want to continue and discard your changes?',
                        )
                    ) {
                        setAvailabilityGroup(availabilityGroupId);
                        setDefaultAvailability(defaultAvailability);
                        setSettings(groupSettings);
                        setIsAvailabilityDirty(false);
                    }
                }}
            />
            <div className="px-4 pb-4 bg-white rounded-b-lg">
                <div className="border-b pt-4 mb-6 pb-6">
                    <div className="input-label">Weekly Hours</div>
                    <form onSubmit={submit}>
                        <AvailabilitySettings
                            settings={settings}
                            setSettings={settings => {
                                setIsAvailabilityDirty(true);
                                setSettings(settings);
                            }}
                            defaultCapacity={resource.capacity}
                            onSave={submit}
                        />

                        <WeeklyHours
                            availability={defaultAvailability}
                            setAvailability={newValue => {
                                setDefaultAvailability(newValue);
                                setIsAvailabilityDirty(true);
                            }}
                            isEditing={isEditing}
                        />
                        {!isEditing && (
                            <button
                                className="btn-light-pill mt-2"
                                onClick={() => setIsEditing(true)}
                            >
                                Edit Hours
                            </button>
                        )}
                        {isAvailabilityDirty && (
                            <button className="btn-save">
                                {isSaving ? 'Saving...' : 'Save Hours'}
                            </button>
                        )}
                    </form>
                </div>
                <div>
                    <div className="input-label">Specific Hours</div>
                    <p>
                        Override your availability for specific dates when hours
                        differ from regular weekly hours.
                    </p>
                    <SpecificHours
                        resource={resource}
                        availability={defaultAvailability}
                        availabilityGroup={availabilityGroup}
                    />
                </div>
            </div>
        </div>
    );
};

export default ResourceAvailability;
